import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BuyerScreening.css';

function BuyerScreening({ buyerId, transactionId, screeningId, userType, onReview }) {
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [uploadError, setUploadError] = useState({});
  const [dragOver, setDragOver] = useState({});
  const [deleting, setDeleting] = useState({});

  const documentTypes = [
    { id: 'income_verification', name: 'Income Verification', description: 'W2s, tax returns, or pay stubs' },
    { id: 'proof_of_funds', name: 'Proof of Funds', description: 'Bank statements showing available funds' },
    { id: 'preapproval_letter', name: 'Pre-approval Letter', description: 'Mortgage pre-approval from lender' },
    { id: 'credit_auth', name: 'Credit Authorization', description: 'Authorization to pull credit report' },
    { id: 'employment_verification', name: 'Employment Verification', description: 'Employment letter or verification' }
  ];

  // Allowed file types and max size
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    fetchScreening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, transactionId, screeningId]);

  const fetchScreening = async () => {
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (screeningId) {
        response = await axios.get(`/api/buyer-screening/${screeningId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (transactionId) {
        response = await axios.get(`/api/buyer-screening/buyer/${buyerId}/transaction/${transactionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.get(`/api/buyer-screening/buyer/${buyerId}/transaction/0`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setScreening(response.data);
    } catch (error) {
      if (error.response?.status === 404 && userType === 'buyer') {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post('/api/buyer-screening/create', {
            buyer_id: buyerId,
            transaction_id: transactionId
          }, { headers: { Authorization: `Bearer ${token}` } });
          setScreening(response.data);
        } catch (e) {
          console.error('Error creating screening:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload PDF, JPG, or PNG files only.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  };

  const handleFileUpload = async (documentType, file) => {
    if (!file) return;
    
    setUploadError({ ...uploadError, [documentType]: null });
    
    const validationError = validateFile(file);
    if (validationError) {
      setUploadError({ ...uploadError, [documentType]: validationError });
      return;
    }
    
    setUploading({ ...uploading, [documentType]: true });
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      
      await axios.post(`/api/buyer-screening/${screening.id}/${documentType}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadError({ ...uploadError, [documentType]: null });
      fetchScreening();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Upload failed. Please try again.';
      setUploadError({ ...uploadError, [documentType]: errorMessage });
    } finally {
      setUploading({ ...uploading, [documentType]: false });
    }
  };

  const handleDeleteDocument = async (documentType) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    setDeleting({ ...deleting, [documentType]: true });
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/buyer-screening/${screening.id}/${documentType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchScreening();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    } finally {
      setDeleting({ ...deleting, [documentType]: false });
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver({ ...dragOver, [documentType]: true });
  };

  const handleDragLeave = (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver({ ...dragOver, [documentType]: false });
  };

  const handleDrop = (e, documentType) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver({ ...dragOver, [documentType]: false });
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileUpload(documentType, files[0]);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'under_review': return 'status-review';
      case 'uploaded': return 'status-uploaded';
      default: return 'status-pending';
    }
  };

  const getOverallProgress = () => {
    if (!screening) return 0;
    let uploaded = 0;
    documentTypes.forEach(doc => {
      const status = screening[`${doc.id}_status`];
      if (status && status !== 'not_uploaded') uploaded++;
    });
    return Math.round((uploaded / documentTypes.length) * 100);
  };

  const getUploadedCount = () => {
    if (!screening) return 0;
    let count = 0;
    documentTypes.forEach(doc => {
      const status = screening[`${doc.id}_status`];
      if (status && status !== 'not_uploaded') count++;
    });
    return count;
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="buyer-screening">
      <div className="screening-header">
        <h3>Document Checklist</h3>
        <div className={`overall-status ${getStatusClass(screening?.overall_status)}`}>
          {screening?.overall_status === 'not_started' || !screening?.overall_status 
            ? 'IN PROGRESS' 
            : screening?.overall_status?.replace('_', ' ').toUpperCase()}
        </div>
      </div>

      {userType === 'buyer' && (
        <div className="screening-info">
          <p>Upload your documents for verification. We'll review them within 1-2 business days.</p>
          
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${getOverallProgress()}%` }}></div>
          </div>
          <p className="progress-text">{getUploadedCount()} of {documentTypes.length} complete</p>
        </div>
      )}

      <div className="documents-list">
        {documentTypes.map(doc => {
          const status = screening?.[`${doc.id}_status`] || 'not_uploaded';
          const url = screening?.[`${doc.id}_url`];
          const rejectionReason = screening?.[`${doc.id}_rejection_reason`];
          const error = uploadError[doc.id];
          const isDragOver = dragOver[doc.id];
          const isUploading = uploading[doc.id];
          const isDeleting = deleting[doc.id];
          
          return (
            <div 
              key={doc.id} 
              className={`document-item ${getStatusClass(status)} ${isDragOver ? 'drag-over' : ''}`}
              onDragOver={(e) => (status === 'not_uploaded' || status === 'rejected') && handleDragOver(e, doc.id)}
              onDragLeave={(e) => handleDragLeave(e, doc.id)}
              onDrop={(e) => (status === 'not_uploaded' || status === 'rejected') && handleDrop(e, doc.id)}
            >
              <div className="document-info">
                <div className={`document-checkbox ${status !== 'not_uploaded' && status !== 'rejected' ? 'checked' : ''} ${status === 'rejected' ? 'rejected' : ''}`}>
                  {status !== 'not_uploaded' && status !== 'rejected' ? '✓' : status === 'rejected' ? '✕' : ''}
                </div>
                <div className="document-details">
                  <h4>{doc.name}</h4>
                  <p>{doc.description}</p>
                  <span className={`status-text ${status}`}>
                    {status === 'not_uploaded' ? 'Not uploaded' : status.replace('_', ' ')}
                  </span>
                  {status === 'rejected' && rejectionReason && (
                    <div className="rejection-reason">
                      <span>Reason: {rejectionReason}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="document-actions">
                {userType === 'buyer' && (
                  <>
                    {status === 'not_uploaded' || status === 'rejected' ? (
                      <div className={`upload-zone ${isDragOver ? 'drag-over' : ''}`}>
                        <label className="upload-btn">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileUpload(doc.id, e.target.files[0])}
                            disabled={isUploading}
                          />
                          {isUploading ? 'Uploading...' : isDragOver ? 'Drop here' : 'Upload'}
                        </label>
                        {!isUploading && !isDragOver && (
                          <span className="drag-hint">or drag & drop</span>
                        )}
                        {error && (
                          <div className="upload-error">{error}</div>
                        )}
                      </div>
                    ) : (
                      <div className="uploaded-actions">
                        <a 
                          href={url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-view"
                        >
                          View
                        </a>
                        {status !== 'approved' && (
                          <button 
                            className="btn-replace"
                            onClick={() => handleDeleteDocument(doc.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? '...' : 'Replace'}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {userType === 'attorney' && url && (
                  <div className="attorney-actions">
                    <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
                      View Document
                    </a>
                    {status === 'uploaded' || status === 'under_review' ? (
                      <>
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={() => onReview && onReview(screening.id, doc.id, 'approved')}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => onReview && onReview(screening.id, doc.id, 'rejected')}
                        >
                          Reject
                        </button>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {screening?.attorney_notes && (
        <div className="attorney-notes">
          <h4>Attorney Notes:</h4>
          <p>{screening.attorney_notes}</p>
        </div>
      )}

      {userType === 'buyer' && screening?.overall_status === 'rejected' && (
        <div className="rejection-notice">
          <h4>Documents Need Attention</h4>
          <p>One or more documents were rejected. Please review the rejection reasons and re-upload.</p>
        </div>
      )}

      {userType === 'buyer' && screening?.overall_status === 'approved' && (
        <div className="approval-notice">
          <h4>All Documents Approved</h4>
          <p>Your documents have been verified. You're ready to proceed.</p>
        </div>
      )}
    </div>
  );
}

export default BuyerScreening;
